import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import './AILimitsManagement.css';
import {
  Sparkles, RefreshCw, Edit2, Save, X, Check, AlertCircle,
  MessageSquare, BarChart3, Users, Crown, Clock, Ban,
  ChevronRight, Info, Search, Shield, ShieldOff
} from 'lucide-react';

const AILimitsManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Active tab: 'chat' or 'analytics'
  const [activeTab, setActiveTab] = useState('chat');

  // Edit modal state
  const [editModal, setEditModal] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editUnlimited, setEditUnlimited] = useState(false);

  // Merchants modal state
  const [merchantsModal, setMerchantsModal] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState('');

  // Block merchant modal
  const [blockModal, setBlockModal] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  // AI Chat Widget limit types
  const chatLimitTypes = [
    { key: 'dailyChatLimit', label: 'Daily Queries', icon: Clock, description: 'Queries per day' },
    { key: 'weeklyChatLimit', label: 'Weekly Queries', icon: Clock, description: 'Queries per week' },
    { key: 'monthlyChatLimit', label: 'Monthly Queries', icon: Clock, description: 'Queries per month' },
  ];

  // AI Analytics limit types
  const analyticsLimitTypes = [
    { key: 'dailyAnalyticsLimit', label: 'Daily Queries', icon: Clock, description: 'Queries per day' },
    { key: 'weeklyAnalyticsLimit', label: 'Weekly Queries', icon: Clock, description: 'Queries per week' },
    { key: 'monthlyAnalyticsLimit', label: 'Monthly Queries', icon: Clock, description: 'Queries per month' },
  ];

  // Storefront AI Chatbot — visitor-facing chat on each merchant's storefront.
  // null = unlimited, 0 = disabled. Service falls back to unlimited when null.
  const chatbotLimitTypes = [
    { key: 'dailyStorefrontChatbotLimit',   label: 'Daily AI Replies',   icon: Clock, description: 'AI replies per merchant per day' },
    { key: 'monthlyStorefrontChatbotLimit', label: 'Monthly AI Replies', icon: Clock, description: 'AI replies per merchant per month' },
  ];

  const limitTypes = activeTab === 'chat'
    ? chatLimitTypes
    : activeTab === 'analytics'
      ? analyticsLimitTypes
      : chatbotLimitTypes;

  // Fetch AI limits data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/ai/limits');
      if (response.success) {
        setTiers(response.tiers || []);
        setSummary(response.summary || null);
      }
    } catch (err) {
      console.error('Error fetching AI limits:', err);
      setError('Failed to load AI limits data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Format limit value for display
  const formatLimit = (value) => {
    if (value === null || value === undefined || value === -1) {
      return 'Unlimited';
    }
    return value.toLocaleString();
  };

  // Check if value is unlimited
  const isUnlimited = (value) => {
    return value === null || value === undefined || value === -1;
  };

  // Open edit modal
  const openEditModal = (tier, limitType) => {
    const value = tier[limitType.key];
    setEditModal({ tier, limitType });
    setEditUnlimited(isUnlimited(value));
    setEditValue(isUnlimited(value) ? '' : value.toString());
    setSuccess('');
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModal(null);
    setEditValue('');
    setEditUnlimited(false);
  };

  // Save limit change
  const handleSaveLimit = async () => {
    if (!editModal) return;

    try {
      setSaving(true);
      setError('');

      const value = editUnlimited ? -1 : parseInt(editValue) || 0;

      const response = await api.patch(`/admin/ai/limits/${editModal.tier.tier}`, {
        limitType: editModal.limitType.key,
        value,
        category: activeTab // 'chat' or 'analytics'
      });

      if (response.success) {
        setSuccess(`Updated ${editModal.limitType.label} for ${editModal.tier.name} tier`);
        closeEditModal();
        fetchData();
      }
    } catch (err) {
      console.error('Error updating limit:', err);
      setError('Failed to update limit');
    } finally {
      setSaving(false);
    }
  };

  // Open merchants modal
  const openMerchantsModal = async (tier) => {
    setMerchantsModal(tier);
    setMerchantsLoading(true);
    setMerchantSearch('');
    try {
      const response = await api.get(`/admin/ai/merchants?tier=${tier.tier}&limit=100`);
      if (response.success) {
        setMerchants(response.merchants || []);
      }
    } catch (err) {
      console.error('Error fetching merchants:', err);
    } finally {
      setMerchantsLoading(false);
    }
  };

  // Block/Unblock merchant
  const handleBlockMerchant = async (merchant, block) => {
    if (block) {
      setBlockModal(merchant);
      setBlockReason('');
    } else {
      try {
        await api.patch(`/admin/ai/merchants/${merchant.id}/block`, {
          blocked: false
        });
        setSuccess(`Unblocked ${merchant.email} from AI features`);
        // Refresh merchants list
        if (merchantsModal) {
          openMerchantsModal(merchantsModal);
        }
      } catch (err) {
        setError('Failed to unblock merchant');
      }
    }
  };

  const confirmBlockMerchant = async () => {
    if (!blockModal) return;
    try {
      await api.patch(`/admin/ai/merchants/${blockModal.id}/block`, {
        blocked: true,
        reason: blockReason || 'Blocked by admin'
      });
      setSuccess(`Blocked ${blockModal.email} from AI features`);
      setBlockModal(null);
      setBlockReason('');
      if (merchantsModal) {
        openMerchantsModal(merchantsModal);
      }
    } catch (err) {
      setError('Failed to block merchant');
    }
  };

  // Get tier color class
  const getTierColor = (tierName) => {
    const colors = {
      'FREE': 'alm-tier-free',
      'STARTER': 'alm-tier-starter',
      'PRO': 'alm-tier-pro',
      'PREMIUM': 'alm-tier-premium',
      'ENTERPRISE': 'alm-tier-enterprise'
    };
    return colors[tierName] || 'alm-tier-default';
  };

  // Filter merchants by search
  const filteredMerchants = merchants.filter(m =>
    m.email?.toLowerCase().includes(merchantSearch.toLowerCase()) ||
    m.storeName?.toLowerCase().includes(merchantSearch.toLowerCase())
  );

  // Auto-clear success message
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="alm-container">
        <div className="alm-loading">
          <RefreshCw className="alm-spinner" size={32} />
          <p>Loading AI limits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alm-container">
      {/* Header */}
      <div className="alm-header">
        <div className="alm-header-left">
          <Sparkles className="alm-header-icon" size={28} />
          <div>
            <h1>AI Limits Management</h1>
            <p>Configure AI query limits for each subscription tier</p>
          </div>
        </div>
        <button className="alm-refresh-btn" onClick={fetchData} disabled={loading}>
          <RefreshCw size={18} className={loading ? 'alm-spinner' : ''} />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alm-alert alm-alert-error">
          <AlertCircle size={18} />
          {error}
          <button onClick={() => setError('')}><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="alm-alert alm-alert-success">
          <Check size={18} />
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="alm-tabs">
        <button
          className={`alm-tab ${activeTab === 'chat' ? 'alm-tab-active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          <MessageSquare size={18} />
          AI Chat Widget
        </button>
        <button
          className={`alm-tab ${activeTab === 'analytics' ? 'alm-tab-active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <BarChart3 size={18} />
          AI Analytics
        </button>
        <button
          className={`alm-tab ${activeTab === 'chatbot' ? 'alm-tab-active' : ''}`}
          onClick={() => setActiveTab('chatbot')}
        >
          <MessageSquare size={18} />
          Storefront Chatbot
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="alm-summary">
          <div className="alm-summary-card">
            <Crown size={24} />
            <div>
              <span className="alm-summary-value">{summary.tierCount || tiers.length}</span>
              <span className="alm-summary-label">Active Tiers</span>
            </div>
          </div>
          <div className="alm-summary-card">
            <Users size={24} />
            <div>
              <span className="alm-summary-value">{(summary.totalAIUsers || 0).toLocaleString()}</span>
              <span className="alm-summary-label">AI Users</span>
            </div>
          </div>
          <div className="alm-summary-card">
            <MessageSquare size={24} />
            <div>
              <span className="alm-summary-value">{(summary.totalQueriesThisMonth || 0).toLocaleString()}</span>
              <span className="alm-summary-label">Queries This Month</span>
            </div>
          </div>
          <div className="alm-summary-card">
            <Ban size={24} />
            <div>
              <span className="alm-summary-value">{summary.blockedMerchants || 0}</span>
              <span className="alm-summary-label">Blocked Users</span>
            </div>
          </div>
        </div>
      )}

      {/* Tier Limits Grid */}
      <div className="alm-grid-container">
        <div className="alm-grid-title">
          {activeTab === 'chat'
            ? 'AI Chat Widget Limits'
            : activeTab === 'analytics'
              ? 'AI Analytics Limits'
              : 'Storefront Chatbot Limits'}
          <span className="alm-grid-subtitle">
            {activeTab === 'chat'
              ? 'Floating chat assistant available to all merchants'
              : activeTab === 'analytics'
                ? 'Premium business intelligence feature'
                : 'Visitor-facing chatbot on each merchant\'s storefront (null = unlimited)'}
          </span>
        </div>
        <div className="alm-grid">
          {/* Header Row */}
          <div className="alm-grid-header">
            <div className="alm-grid-cell alm-label-cell">Limit Type</div>
            {tiers.map(tier => (
              <div key={tier.tier} className={`alm-grid-cell alm-tier-header ${getTierColor(tier.tier)}`}>
                <span className="alm-tier-name">{tier.name}</span>
                <span className="alm-tier-price">
                  {tier.monthlyPrice === 0 ? 'Free' : `₦${tier.monthlyPrice?.toLocaleString()}/mo`}
                </span>
                <button
                  className="alm-merchants-btn"
                  onClick={() => openMerchantsModal(tier)}
                >
                  {tier.merchantCount || 0} merchants <ChevronRight size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Limit Rows */}
          {limitTypes.map(limitType => (
            <div key={limitType.key} className="alm-grid-row">
              <div className="alm-grid-cell alm-label-cell">
                <limitType.icon size={18} />
                <div>
                  <span className="alm-limit-name">{limitType.label}</span>
                  <span className="alm-limit-desc">{limitType.description}</span>
                </div>
              </div>
              {tiers.map(tier => (
                <div key={`${tier.tier}-${limitType.key}`} className="alm-grid-cell alm-value-cell">
                  <span className={`alm-limit-value ${isUnlimited(tier[limitType.key]) ? 'alm-unlimited' : ''}`}>
                    {formatLimit(tier[limitType.key])}
                  </span>
                  <button
                    className="alm-edit-btn"
                    onClick={() => openEditModal(tier, limitType)}
                    title={`Edit ${limitType.label} for ${tier.name}`}
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="alm-info-box">
        <Info size={18} />
        <div>
          <strong>How AI limits work:</strong>
          <p>
            <strong>AI Chat Widget:</strong> A floating assistant available to all merchants for quick questions.
            Limits are enforced daily and reset at midnight.
          </p>
          <p>
            <strong>AI Analytics:</strong> Premium business intelligence feature (PRO+ tiers).
            Feature-gated for FREE/STARTER tiers, unlimited for paid tiers by default.
          </p>
          <p>
            Set a value to <strong>-1</strong> or leave empty for <strong>Unlimited</strong>.
            You can block individual merchants from AI features if needed.
          </p>
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="alm-modal-overlay" onClick={closeEditModal}>
          <div className="alm-modal" onClick={e => e.stopPropagation()}>
            <div className="alm-modal-header">
              <h2>Edit {editModal.limitType.label}</h2>
              <button className="alm-modal-close" onClick={closeEditModal}>
                <X size={20} />
              </button>
            </div>
            <div className="alm-modal-body">
              <div className={`alm-modal-tier-badge ${getTierColor(editModal.tier.tier)}`}>
                {editModal.tier.name} Tier
              </div>

              <div className="alm-form-group">
                <label className="alm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={editUnlimited}
                    onChange={e => setEditUnlimited(e.target.checked)}
                  />
                  <span>Unlimited queries</span>
                </label>
              </div>

              {!editUnlimited && (
                <div className="alm-form-group">
                  <label>
                    {editModal.limitType.label} Limit
                    <span className="alm-form-hint">Number of queries allowed</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    placeholder="Enter limit"
                    className="alm-input"
                  />
                </div>
              )}

              <div className="alm-impact-info">
                <AlertCircle size={16} />
                <span>
                  This change affects all <strong>{editModal.tier.merchantCount || 0}</strong> merchants on the {editModal.tier.name} tier
                </span>
              </div>
            </div>
            <div className="alm-modal-footer">
              <button className="alm-btn alm-btn-secondary" onClick={closeEditModal}>
                Cancel
              </button>
              <button
                className="alm-btn alm-btn-primary"
                onClick={handleSaveLimit}
                disabled={saving}
              >
                {saving ? <RefreshCw size={16} className="alm-spinner" /> : <Save size={16} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merchants Modal */}
      {merchantsModal && (
        <div className="alm-modal-overlay" onClick={() => setMerchantsModal(null)}>
          <div className="alm-modal alm-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="alm-modal-header">
              <h2>{merchantsModal.name} Tier - AI Usage</h2>
              <button className="alm-modal-close" onClick={() => setMerchantsModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="alm-modal-body">
              {/* Search */}
              <div className="alm-search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search merchants..."
                  value={merchantSearch}
                  onChange={e => setMerchantSearch(e.target.value)}
                />
              </div>

              {merchantsLoading ? (
                <div className="alm-loading-inline">
                  <RefreshCw className="alm-spinner" size={20} />
                  Loading merchants...
                </div>
              ) : filteredMerchants.length === 0 ? (
                <div className="alm-empty-state">
                  <Users size={48} />
                  <p>{merchantSearch ? 'No merchants match your search' : 'No merchants on this tier'}</p>
                </div>
              ) : (
                <div className="alm-merchants-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Store</th>
                        <th>Chat Usage</th>
                        <th>Analytics Usage</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMerchants.map(m => (
                        <tr key={m.id} className={m.isBlocked ? 'alm-row-blocked' : ''}>
                          <td>
                            <div className="alm-merchant-info">
                              <span className="alm-merchant-email">{m.email}</span>
                              <span className="alm-merchant-username">@{m.username || 'N/A'}</span>
                            </div>
                          </td>
                          <td>{m.storeName || '-'}</td>
                          <td>
                            <span className="alm-usage-value">
                              {m.chatQueriesThisMonth || 0}
                              <span className="alm-usage-period">/month</span>
                            </span>
                          </td>
                          <td>
                            <span className="alm-usage-value">
                              {m.analyticsQueriesThisMonth || 0}
                              <span className="alm-usage-period">/month</span>
                            </span>
                          </td>
                          <td>
                            {m.isBlocked ? (
                              <span className="alm-badge alm-badge-danger">
                                <Ban size={12} /> Blocked
                              </span>
                            ) : (
                              <span className="alm-badge alm-badge-success">
                                <Check size={12} /> Active
                              </span>
                            )}
                          </td>
                          <td>
                            {m.isBlocked ? (
                              <button
                                className="alm-action-btn alm-action-unblock"
                                onClick={() => handleBlockMerchant(m, false)}
                                title="Unblock from AI"
                              >
                                <Shield size={16} />
                              </button>
                            ) : (
                              <button
                                className="alm-action-btn alm-action-block"
                                onClick={() => handleBlockMerchant(m, true)}
                                title="Block from AI"
                              >
                                <ShieldOff size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="alm-modal-footer">
              <button className="alm-btn alm-btn-secondary" onClick={() => setMerchantsModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Merchant Modal */}
      {blockModal && (
        <div className="alm-modal-overlay" onClick={() => setBlockModal(null)}>
          <div className="alm-modal" onClick={e => e.stopPropagation()}>
            <div className="alm-modal-header">
              <h2>Block Merchant from AI</h2>
              <button className="alm-modal-close" onClick={() => setBlockModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="alm-modal-body">
              <div className="alm-block-warning">
                <Ban size={24} />
                <p>
                  You are about to block <strong>{blockModal.email}</strong> from all AI features.
                  They will not be able to use the AI Chat Widget or AI Analytics.
                </p>
              </div>

              <div className="alm-form-group">
                <label>
                  Reason for blocking
                  <span className="alm-form-hint">Optional - will be recorded in logs</span>
                </label>
                <textarea
                  value={blockReason}
                  onChange={e => setBlockReason(e.target.value)}
                  placeholder="e.g., Abuse of AI features, Terms violation..."
                  className="alm-textarea"
                  rows={3}
                />
              </div>
            </div>
            <div className="alm-modal-footer">
              <button className="alm-btn alm-btn-secondary" onClick={() => setBlockModal(null)}>
                Cancel
              </button>
              <button
                className="alm-btn alm-btn-danger"
                onClick={confirmBlockMerchant}
              >
                <Ban size={16} />
                Block Merchant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AILimitsManagement;
